// ============================================================================
// Fee Service
// Manages site and store overage fees and charges
// ============================================================================

import {
  SiteOverageFee,
  SiteOverageFeeCreate,
  SiteOverageFeeUpdate,
  StoreOverageFee,
  StoreOverageFeeCreate,
  StoreOverageFeeUpdate,
  SiteImposedCharge,
  SiteImposedChargeCreate,
  SiteImposedChargeUpdate,
  StoreCharge,
} from '../types/site';
import { fetchWithAuth } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// Site Overage Fees
// ============================================================================

export async function getSiteOverageFees(siteId: number): Promise<SiteOverageFee[]> {
  const params = new URLSearchParams({ site_id: siteId.toString() });
  return fetchWithAuth(`${API_BASE_URL}/site-overage-fees/?${params}`);
}

export async function createSiteOverageFee(data: SiteOverageFeeCreate): Promise<SiteOverageFee> {
  return fetchWithAuth(`${API_BASE_URL}/site-overage-fees/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSiteOverageFee(feeId: number, data: SiteOverageFeeUpdate): Promise<SiteOverageFee> {
  return fetchWithAuth(`${API_BASE_URL}/site-overage-fees/${feeId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSiteOverageFee(feeId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/site-overage-fees/${feeId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Store Overage Fees
// ============================================================================

export async function getStoreOverageFees(storeId?: number): Promise<StoreOverageFee[]> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/store-overage-fees/${queryString ? '?' + queryString : ''}`);
}

export async function createStoreOverageFee(data: StoreOverageFeeCreate, storeId?: number): Promise<StoreOverageFee> {
  const params = new URLSearchParams();
  if (storeId) params.append('store_id', storeId.toString());
  const queryString = params.toString();
  return fetchWithAuth(`${API_BASE_URL}/store-overage-fees/${queryString ? '?' + queryString : ''}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateStoreOverageFee(feeId: number, data: StoreOverageFeeUpdate): Promise<StoreOverageFee> {
  return fetchWithAuth(`${API_BASE_URL}/store-overage-fees/${feeId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteStoreOverageFee(feeId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/store-overage-fees/${feeId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Site Imposed Charges
// ============================================================================

export async function getSiteCharges(siteId: number): Promise<SiteImposedCharge[]> {
  const params = new URLSearchParams({ site_id: siteId.toString() });
  return fetchWithAuth(`${API_BASE_URL}/site-charges/?${params}`);
}

export async function createSiteCharge(data: SiteImposedChargeCreate): Promise<SiteImposedCharge> {
  return fetchWithAuth(`${API_BASE_URL}/site-charges/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSiteCharge(chargeId: number, data: SiteImposedChargeUpdate): Promise<SiteImposedCharge> {
  return fetchWithAuth(`${API_BASE_URL}/site-charges/${chargeId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSiteCharge(chargeId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/site-charges/${chargeId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Store Charges
// ============================================================================

export async function getStoreCharges(storeCode: string): Promise<StoreCharge[]> {
  return fetchWithAuth(`${API_BASE_URL}/charges/public?store_code=${storeCode}`);
}

// ============================================================================
// Named Export Object
// ============================================================================

export const feeService = {
  // Site Overage Fees
  getSiteOverageFees,
  createSiteOverageFee,
  updateSiteOverageFee,
  deleteSiteOverageFee,
  // Store Overage Fees
  getStoreOverageFees,
  createStoreOverageFee,
  updateStoreOverageFee,
  deleteStoreOverageFee,
  // Site Charges
  getSiteCharges,
  createSiteCharge,
  updateSiteCharge,
  deleteSiteCharge,
  // Store Charges
  getStoreCharges,
};

export default feeService;
